import type { UrlObject } from 'url';
import { ParsedUrlQuery } from 'node:querystring';
import { LinkProps } from 'next/link';
import {
  READY_DESKTOP_ROUTES_FOR_PRODUCTION,
  READY_ROUTES_FOR_PRODUCTION,
  ROUTES,
} from '@/constants/routes';
import { isDeepLink, makeUrlWithLang, startsWithHttp } from '@/utils/helpers';
import { mapQueryStringToPaymentData } from '@/views/Wallet/Finish.helpers';
import { CONFIG } from '@/constants/config';

export function buildURL(data: UrlObject) {
  const baseUrl = window.location.origin;
  const url = new URL(baseUrl + (data.pathname || ''));

  if (data.query) {
    for (const [key, value] of Object.entries(data.query)) {
      if (value != null) {
        url.searchParams.append(key, value.toString());
      }
    }
  }

  if (data.hash) {
    url.hash = data.hash;
  }

  return url.href;
}

type AdRouteKeys =
  | 'main'
  | 'category'
  | 'subcategory'
  | 'vip'
  | 'new'
  | 'adDetails'
  | 'userAds'
  | 'shopDetails'
  | 'edit';

type CollectionRouteKeys = 'collections';

export function matchCollectionRoute(
  url: string,
  keysToCheck: CollectionRouteKeys[]
) {
  const routes: Record<CollectionRouteKeys, RegExp> = {
    collections: /^\/collections(\/[A-Za-z0-9_-]+)?$/,
  };

  return Object.keys(routes).some(key => {
    const routeKey = key as CollectionRouteKeys;
    if (routes[routeKey].test(url)) {
      return keysToCheck.includes(routeKey);
    }
    return false;
  });
}

export function matchAdRoute(url: string, keysToCheck: AdRouteKeys[]) {
  const routes: Record<AdRouteKeys, RegExp> = {
    main: /^\/elanlar(\?.*)?$/,
    new: /^\/elanlar(?:\/[^/]+(?:\/[^/]+)?)?\/new$/,
    vip: /^\/elanlar(?:\/[^/]+(?:\/[^/]+)?)?\/vip$/,
    category: /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+$/,
    subcategory:
      /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/(?!new$|vip$|lightweight_form$)[^/]+$/,
    adDetails:
      /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/[^/]+\/\d+(?:[?#].*)?$/,
    userAds: /^\/elanlar\?user_id=\d+$/,
    shopDetails: /^\/shops\/[^/]+$/,
    edit: /^\/elanlar\/(?!new$|vip$|lightweight_form$)[^/]+\/[^/]+\/\d+\/ad_edits$/,
  };

  return Object.keys(routes).some(key => {
    const routeKey = key as AdRouteKeys;
    if (routes[routeKey].test(url)) {
      return keysToCheck.includes(routeKey);
    }
    return false;
  });
}

export function checkRoute(
  href: LinkProps['href'],
  options?: { query?: ParsedUrlQuery; isDesktop?: boolean; lang?: string }
): {
  isInternal?: boolean;
  hrefString: string;
} {
  const { query, isDesktop, lang } = options ?? {};
  const isHrefString = typeof href === 'string';
  const hrefString = isHrefString ? href : href.pathname || buildURL(href);

  const isInternal = !!READY_ROUTES_FOR_PRODUCTION.find(r => {
    if (r === ROUTES.ad && hrefString.includes(r)) {
      if (query?.cursor) return false; // for infinite scroll on the rails shop page

      if (isDesktop) {
        return matchAdRoute(hrefString, ['new', 'edit']);
      }

      return matchAdRoute(hrefString, [
        'main',
        'category',
        'subcategory',
        'adDetails',
        'userAds',
        'new',
        'edit',
      ]);
    }

    if (r === ROUTES.collections && hrefString.includes(r)) {
      return matchCollectionRoute(hrefString, ['collections']);
    }

    if (r === ROUTES.walletFinish && hrefString.includes(r)) {
      return Boolean(query && mapQueryStringToPaymentData(query));
    }

    if (r === ROUTES.shops && hrefString.includes(r)) {
      return matchAdRoute(hrefString, ['shopDetails']);
    }

    if (isDesktop) {
      return (
        r === hrefString && READY_DESKTOP_ROUTES_FOR_PRODUCTION.includes(r)
      );
    }

    return r === hrefString;
  });

  if (!isInternal && !startsWithHttp(hrefString) && !isDeepLink(hrefString)) {
    const url = makeUrlWithLang({
      protocol: CONFIG.protocol,
      host: CONFIG.host,
      path: hrefString,
      lang,
    });
    return { isInternal, hrefString: url };
  }

  return { isInternal, hrefString };
}
